import {
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { componentForwardRef } from '../../common/component-forward-ref';
import { BsDatepickerInlineConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { DateTime } from 'luxon';
import { PopoverDirective, PopoverModule } from 'ngx-bootstrap/popover';
import { ShortHandParserDirective } from '../../common/directives/short-hand-parser.directive';
import { InputMaskDirective } from '../../common/directives/input-mask.directive';
import { NgClass } from '@angular/common';

/**
 * This component (ema-datepicker) represents a datepicker with MM styling.
 */
@Component({
    selector: 'lib-mm-datepicker',
    standalone: true,
    imports: [BsDatepickerModule, FormsModule, PopoverModule,
        InputMaskDirective, ShortHandParserDirective, NgClass],
    templateUrl: './mm-datepicker.component.html',
    styleUrl: './mm-datepicker.component.scss',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: componentForwardRef(MmDatepickerComponent),
        multi: true
    },
    {
        provide: NG_VALIDATORS,
        useExisting: componentForwardRef(MmDatepickerComponent),
        multi: true
    }]
})
export class MmDatepickerComponent implements OnInit, OnChanges, Validator, ControlValueAccessor {

    /**
     * Html ID to be used by the input representing a datepicker.
     */
    @Input() inputId: string | undefined;

    /**
     * Minimum date that can be selected.
     */
    @Input() minDate: Date | null = null;

    /**
     * Maximum date that can be selected
     */
    @Input() maxDate: Date | null = null;

    /*
    * Dates can be selected for other months from the current month
    * */
    @Input() selectFromOtherMonth = false;

    /**
     * Set field as required
     */
    @Input() required = false;

    /**
     * Whether datepicker is currently disabled
     */
    @Input() @HostBinding('class.datepicker-disabled') disabled = false;

    /**
     * Whether datepicker is read only
     */
    @Input() @HostBinding('class.datepicker-readonly') readonly = false;

    /**
     * A selector specifying the element the popover should be appended to.
     */
    @Input() appendTo: string | undefined;

    @Input() placeholder = 'mm/dd/yyyy';

    @Input() isInvalid = false;

    model!: Date | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pickerModel!: any;
    inputModel!: string | null;

    dateFormat = 'MM/dd/yyyy';
    datepickerConfig: Partial<BsDatepickerInlineConfig> = {
        showWeekNumbers: false,
        customTodayClass: 'today'
    };

    @Input() allowShortcuts = true;
    @Input() showTooltip: boolean | undefined;
    @Output() inputBlur = new EventEmitter<void>();
    private browserTimeZone = DateTime.local().zoneName;
    datePickerInputMask!: string;

    ngOnInit(): void {
        this.inputModel = this.inputModel ?? '';
        this.datePickerInputMask = this.allowShortcuts ? '(99/99/9999)|(a+9{+})' : '99/99/9999';
        this.datepickerConfig.selectFromOtherMonth = this.selectFromOtherMonth;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes[ 'minDate' ]) {
            this.validateModel();
            const minDate = this.toPickerDateTime(this.minDate)?.toJSDate();
            this.datepickerConfig.minDate = minDate ?? undefined;
        }
        if (changes[ 'maxDate' ]) {
            this.validateModel();
            const maxDate = this.toPickerDateTime(this.maxDate)?.toJSDate();
            this.datepickerConfig.maxDate = maxDate ?? undefined;
        }
    }

    registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(val: Date | null): void {
        this.setModel(val, false); // don't fire form value change event if due to an external value change
        this.setInputModel(val);
    }

    showPicker(popover: PopoverDirective): void {
        if (!this.disabled) {
            popover.show();
        }
    }

    onKeydown() {
        // NOOP
    }

    updateDate(date: DateTime): void {
        if (date?.isValid) {
            this.inputModel = date.toFormat(this.dateFormat);
            this.setModel(date.toJSDate());
        } else {
            this.setModel(new Date(''));
        }
    }

    onInputModelChange(val: string): void {
        this.inputModel = val;

        if (val) {
            const newDate = DateTime.fromFormat(val, this.dateFormat);
            const newModel = newDate.isValid ?
                this.toModelDateTime(newDate.toJSDate())?.toJSDate() ?? null :
                null;
            this.setModel(newModel);
            return;
        }

        this.setModel(null);
    }

    onPickerChange(val: Date | null, popover?: PopoverDirective): void {
        if (val?.getTime() !== this.pickerModel?.getTime()) {
            const newModel = this.toModelDateTime(val)?.toJSDate() ?? null;
            this.setInputModel(newModel);
            this.setModel(newModel);
            this.hidePicker(popover);
            this.inputBlur.emit();
        }
    }

    hidePicker(popover?: PopoverDirective): void {
        popover?.hide();
    }

    onInputBlur(): void {
        this.onTouch();
        this.inputBlur.emit();
    }

    validate(): ValidationErrors | null {
        return this.validateModel();
    }

    private validateModel(): ValidationErrors | null {
        if (this.inputModel?.length) {
            const dt = DateTime.fromFormat(this.inputModel, this.dateFormat);
            const shortHandPattern = /^[dwmy]\+\d+$/i;

            if (!dt.isValid && !shortHandPattern.test(this.inputModel)) {
                return {
                    validDate: false
                };
            }
        }
        if (this.model) {
            const dt = DateTime.fromJSDate(this.model);

            if (this.minDate) {
                const minDateTime = DateTime.fromJSDate(this.minDate);
                if (minDateTime > dt) {
                    return {
                        minDate: { minDate: minDateTime.toFormat(this.dateFormat), actualDate: this.inputModel }
                    };
                }
            }

            if (this.maxDate) {
                const maxDateTime = DateTime.fromJSDate(this.maxDate);
                if (this.maxDate) {
                    if (maxDateTime < dt) {
                        return {
                            maxDate: { maxDate: maxDateTime.toFormat(this.dateFormat), actualDate: this.inputModel }
                        };
                    }
                }
            }
        } else if (this.required) {
            return {
                required: true
            };
        }

        return null;
    }

    private setInputModel(val: Date | null): void {
        this.inputModel = this.toPickerDateTime(val)?.toFormat(this.dateFormat) ?? null;
    }

    /**
     * `fireOnChange` should be passed in as `false` when called from `writeValue`, since Angular handles
     * propagating that to the `valueChanges` event. This allows the caller of setValue/patchValue to control
     * the event emission properly with the `emitEvent` option.
     */
    private setModel(value: Date | null, fireOnChange = true): void {
        const pickerDateTime = this.toPickerDateTime(value);
        const pickerDate = pickerDateTime?.toJSDate() ?? null;

        const currentModel = this.model;
        const newModel = this.toModelDateTime(pickerDate)?.toJSDate() ?? null;
        const modelChanged = newModel?.getTime() !== currentModel?.getTime();

        if (modelChanged) {
            this.model = newModel;
            this.pickerModel = pickerDateTime?.isValid ? pickerDate : null;

            if (fireOnChange) {
                this.onChange(this.model);
            }
        }
    }

    private toModelDateTime(d: Date | null): DateTime | null {
        const dt = d ? DateTime.fromJSDate(d) : null;
        if (!dt?.isValid) {
            return null;
        }
        return dt.startOf('day');
    }

    private toPickerDateTime(d: Date | null): DateTime | null {
        const dt = d ? DateTime.fromJSDate(d) : null;
        if (!dt?.isValid) {
            return null;
        }
        return dt.startOf('day').setZone(this.browserTimeZone);
    }

    stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    private onChange: (v: Date | null) => void = () => {
        // NOOP
    };

    private onTouch: () => void = () => {
        // NOOP
    };


}
